import { createContext, useContext, useMemo, useState, useCallback, type ReactNode } from "react";
import { useAgentTabs, useActiveAgent, type AgentTab } from "../hooks/useAgentTabs";
import {
  useConversationTabsQuery,
  useActiveConversation,
  type ConversationTab,
} from "../hooks/useConversationTabs";
import { useChatRailWidth, useChatRailCollapsed } from "../hooks/useChatRailWidth";
import { useHasAnyRole } from "../../../hooks/useHasRole";

interface ChatRailContextValue {
  // Visibility
  available: boolean; // false for observer / unauthenticated
  collapsed: boolean;
  setCollapsed: (next: boolean) => void;
  toggleCollapsed: () => void;

  // Width
  widthPx: number;
  setWidthPx: (next: number) => void;
  isResizing: boolean;
  setIsResizing: (next: boolean) => void;
  MIN_W: number;
  MAX_W: number;
  DEFAULT_W: number;

  // Agents
  agentTabs: AgentTab[];
  agentTabsLoading: boolean;
  activeAgentId: string | null;
  setActiveAgentId: (id: string | null) => void;

  // Conversations (for active agent)
  conversationTabs: ConversationTab[];
  conversationTabsLoading: boolean;
  activeConvoId: string | null;
  setActiveConvoId: (id: string | null) => void;

  // Run drawer
  drawerRunId: string | null;
  setDrawerRunId: (id: string | null) => void;
}

const ChatRailContext = createContext<ChatRailContextValue | null>(null);

export function ChatRailProvider({ children }: { children: ReactNode }) {
  const isObserver = useHasAnyRole("manna", ["observer"]);
  const available = !isObserver;

  const { tabs: agentTabs, isLoading: agentTabsLoading } = useAgentTabs();
  const { activeAgentId, setActiveAgentId } = useActiveAgent(agentTabs);

  const { data: conversationTabsData = [], isLoading: conversationTabsLoading } =
    useConversationTabsQuery(activeAgentId);
  const { activeConvoId, setActiveConvoId } = useActiveConversation(
    activeAgentId,
    conversationTabsData,
  );

  const { widthPx, setWidthPx, isResizing, setIsResizing, MIN_W, MAX_W, DEFAULT_W } =
    useChatRailWidth();
  const { collapsed, setCollapsed, toggle: toggleCollapsed } = useChatRailCollapsed();

  const [drawerRunId, setDrawerRunIdState] = useState<string | null>(null);
  const setDrawerRunId = useCallback((id: string | null) => setDrawerRunIdState(id), []);

  const value = useMemo<ChatRailContextValue>(
    () => ({
      available,
      collapsed,
      setCollapsed,
      toggleCollapsed,

      widthPx,
      setWidthPx,
      isResizing,
      setIsResizing,
      MIN_W,
      MAX_W,
      DEFAULT_W,

      agentTabs,
      agentTabsLoading,
      activeAgentId,
      setActiveAgentId,

      conversationTabs: conversationTabsData,
      conversationTabsLoading,
      activeConvoId,
      setActiveConvoId,

      drawerRunId,
      setDrawerRunId,
    }),
    [
      available,
      collapsed,
      setCollapsed,
      toggleCollapsed,
      widthPx,
      setWidthPx,
      isResizing,
      setIsResizing,
      MIN_W,
      MAX_W,
      DEFAULT_W,
      agentTabs,
      agentTabsLoading,
      activeAgentId,
      setActiveAgentId,
      conversationTabsData,
      conversationTabsLoading,
      activeConvoId,
      setActiveConvoId,
      drawerRunId,
      setDrawerRunId,
    ],
  );

  return <ChatRailContext.Provider value={value}>{children}</ChatRailContext.Provider>;
}

export function useChatRail(): ChatRailContextValue {
  const ctx = useContext(ChatRailContext);
  if (!ctx) throw new Error("useChatRail must be used inside ChatRailProvider");
  return ctx;
}
